import { useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { CheckCircle2, XCircle } from "lucide-react";
import { lt } from "../localize";
import type { QuizData } from "../types";

interface QuizQuestionProps {
  quiz: QuizData;
  onAnswer: (selectedIndex: number, isCorrect: boolean) => void;
}

export const QuizQuestion = ({ quiz, onAnswer }: QuizQuestionProps) => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const [selected, setSelected] = useState<number | null>(null);
  const answered = selected !== null;

  const handleSelect = (index: number) => {
    if (answered) return;
    setSelected(index);
    onAnswer(index, quiz.options[index].isCorrect);
  };

  return (
    <div className="space-y-4">
      <h4 className="font-bold text-sm">{lt(quiz.question, lang)}</h4>

      <div className="space-y-2" role="radiogroup">
        {quiz.options.map((option, i) => {
          const isSelected = selected === i;
          const isCorrectOption = option.isCorrect;
          const showCorrect = answered && isCorrectOption;
          const showWrong = answered && isSelected && !isCorrectOption;

          return (
            <button
              key={i}
              role="radio"
              aria-checked={isSelected}
              aria-label={
                showCorrect
                  ? `${lt(option.text, lang)} — correct answer`
                  : showWrong
                    ? `${lt(option.text, lang)} — wrong answer`
                    : undefined
              }
              className={cn(
                "w-full text-left p-3.5 rounded-xl text-sm transition-all duration-200",
                "flex items-center gap-3",
                !answered && "glass cursor-pointer hover:bg-white/[0.07] hover:border-secondary/40",
                answered && "glass cursor-default",
                showCorrect && "!border-primary !bg-primary/10",
                showWrong && "!border-destructive !bg-destructive/10",
              )}
              style={
                showCorrect
                  ? { animation: "correctPulse 0.6s ease-out" }
                  : showWrong
                    ? { animation: "wrongShake 0.4s ease-out" }
                    : undefined
              }
              onClick={() => handleSelect(i)}
              disabled={answered}
            >
              {showCorrect && (
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
              )}
              {showWrong && (
                <XCircle className="w-5 h-5 text-destructive shrink-0" />
              )}
              {!showCorrect && !showWrong && (
                <span className={cn(
                  "w-5 h-5 rounded-full border-2 shrink-0 transition-colors",
                  isSelected ? "border-secondary bg-secondary" : "border-white/20"
                )} />
              )}
              <span className="leading-relaxed">{lt(option.text, lang)}</span>
            </button>
          );
        })}
      </div>

      {answered && (
        <div
          role="alert"
          className={cn(
            "glass p-4 text-sm leading-relaxed border-l-4",
            quiz.options[selected].isCorrect
              ? "border-l-primary"
              : "border-l-destructive",
          )}
          style={{ animation: "slideInUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards" }}
        >
          {lt(quiz.explanation, lang)}
        </div>
      )}
    </div>
  );
};
